import React, { useMemo } from "react";
import { AlbumWithShare } from "../types/album";
import { formatDistanceToNow } from "date-fns";
import AuthenticatedImage from "./AuthenticatedImage";

interface AlbumListProps {
  albums: AlbumWithShare[];
  onAlbumClick: (album: AlbumWithShare) => void;
  onDeleteAlbum?: (album: AlbumWithShare) => void;
  isLoading?: boolean;
}

const AlbumList: React.FC<AlbumListProps> = ({ albums, onAlbumClick, onDeleteAlbum, isLoading = false }) => {
  const sortedAlbums = useMemo(
    () =>
      [...albums].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [albums],
  );

  if (isLoading) {
    return (
      <div data-section="album-list-root" className="grid gap-4 sm:gap-5 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className="rounded-2xl overflow-hidden bg-white/8 border border-white/10 shadow-xl animate-pulse"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            {/* Cover skeleton */}
            <div className="aspect-[4/3] bg-white/10" />
            {/* Info skeleton */}
            <div className="p-3 sm:p-4 space-y-2">
              <div className="h-5 bg-white/15 rounded-lg w-3/4" />
              <div className="h-3 bg-white/10 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (albums.length === 0) {
    return (
      <div data-section="album-list-root" className="text-center py-24">
        <div className="max-w-md mx-auto">
          <div className="relative mb-8">
            <div className="w-28 h-28 mx-auto rounded-full flex items-center justify-center relative overflow-hidden jp-glass shadow-xl">
              <div className="absolute inset-0 jp-gradient-brand-soft opacity-70"></div>
              <svg
                className="w-14 h-14 text-white/90 relative z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
          </div>

          <h3 className="text-3xl font-extrabold text-white/95 mb-4 tracking-tight">Your canvas awaits</h3>
          <p className="text-base sm:text-lg text-white/70 mb-8 leading-relaxed">
            Create your first album and start building a shared place for the moments that matter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-section="album-list-root">
      <div className="grid gap-4 sm:gap-5 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {sortedAlbums.map((album, index) => (
          <div
            key={album.id}
            onClick={() => onAlbumClick(album)}
            className="group cursor-pointer min-w-0"
            style={{
              animationDelay: `${index * 80}ms`,
              animation: "fadeInUp 0.5s ease-out forwards",
            }}
          >
            <div className="relative w-full overflow-hidden rounded-2xl bg-white/8 border border-white/10 shadow-xl transition-all duration-300 sm:hover:shadow-2xl sm:hover:shadow-black/40 sm:hover:-translate-y-1 sm:hover:border-white/20">

              {/* Cover Photo — 4:3 crop */}
              <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[rgba(var(--primary-green-rgb),0.18)] to-[rgba(var(--primary-blue-rgb),0.20)]">
                {album.thumbnailUrl ? (
                  <AuthenticatedImage
                    src={album.thumbnailUrl}
                    alt={`${album.name} cover`}
                    className="w-full h-full object-cover transition-transform duration-500 sm:group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 sm:w-16 sm:h-16 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}

                {/* Gradient overlay on photo for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                {/* Media count pill — bottom-left of photo */}
                <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1 text-white text-xs font-semibold">
                  <svg className="w-3.5 h-3.5 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                  </svg>
                  {album.mediaCount}
                </div>

                {/* Shared badge — bottom-right of photo */}
                {album.isShared && (
                  <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-[rgba(var(--primary-blue-rgb),0.7)] backdrop-blur-sm rounded-full px-2.5 py-1 text-white text-xs font-semibold">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Shared
                  </div>
                )}

                {/* New uploads indicator — top-right */}
                {album.hasNewMediaUploads && (
                  <div className="absolute top-2.5 right-2.5" aria-label="New media uploads" title="New media uploads">
                    <div className="h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.25),0_0_12px_rgba(52,211,153,0.8)]" />
                  </div>
                )}
              </div>

              {/* Info row below photo */}
              <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 min-w-0">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-bold text-white/95 truncate leading-tight">
                    {album.name}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-white/45 mt-0.5 font-medium">
                    {formatDistanceToNow(new Date(album.createdAt), { addSuffix: true })}
                  </p>
                </div>

                {/* Delete button — only for owned albums */}
                {onDeleteAlbum && !album.isShared && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAlbum(album);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="ml-2 flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-red-500/15 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    aria-label="Delete album"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlbumList;
